import React, { useEffect, useState } from 'react';
import { Card } from '@app/components/common/Card/Card';
import { Form, Col, Row, message, Typography, Space, UploadProps, Upload, Image, Radio, Switch } from 'antd';
import { Input } from '@app/components/common/inputs/Input/Input';
import TextArea from 'antd/lib/input/TextArea';
import { Button } from '@app/components/common/buttons/Button/Button';
import { UploadOutlined } from '@ant-design/icons';
import dayjs from 'dayjs';
import { useLocation, useNavigate } from 'react-router-dom';
import { DatePicker } from '@app/components/common/pickers/DatePicker';
import { ContentModel } from '@app/models/ContentModel';
import { getProfilePhoto } from '@app/utils/utils';
import { postContent, updateContent } from '@app/api/admin.api';
import { notificationController } from '@app/controllers/notificationController';
import { Checkbox } from '@app/components/common/Checkbox/Checkbox';
import ReactQuill, { Quill } from 'react-quill';
import 'react-quill/dist/quill.snow.css';
import './custom-quill.css';
import htmlEditButton from 'quill-html-edit-button';

Quill.register('modules/htmlEditButton', htmlEditButton);

const Font = Quill.import('formats/font') as any;
Font.whitelist = ['serif', 'monospace', 'arial', 'times-new-roman', 'courier-new', 'georgia', 'verdana'];
Quill.register(Font, true);

export interface ContentRequest {
  image: string | null;
  title: string | null;
  body: string | null;
  available_in_about_screen: boolean;
  available_in_explore_screen: boolean;
}

const initialContentValues: ContentRequest = {
  title: '',
  body: '',
  image: null,
  available_in_about_screen: false,
  available_in_explore_screen: false,
};

interface LocationState {
  record: ContentModel;
}

// Quill modules and formats configuration
const quillModules = {
  toolbar: [
    [{ header: [1, 2, 3, 4, false] }, { font: ['', 'serif', 'monospace', 'arial', 'times-new-roman', 'courier-new', 'georgia', 'verdana'] }, { size: [] }],
    ['bold', 'italic', 'underline', 'strike'],
    [{ script: 'sub' }, { script: 'super' }],
    [{ color: [] }, { background: [] }],
    [{ align: [] }],
    [{ list: 'ordered' }, { list: 'bullet' }, { indent: '-1' }, { indent: '+1' }],
    ['blockquote', 'code-block'],
    ['link', 'image', 'video'],
    ['clean'],
  ],
  htmlEditButton: {
    debug: false,
    msg: 'Edit the HTML content',
    okText: 'Save',
    cancelText: 'Cancel',
    buttonHTML: '&lt;/&gt;',
    buttonTitle: 'Show HTML source',
    syntax: false,
  },
};

const quillFormats = [
  'header', 'font', 'size',
  'bold', 'italic', 'underline', 'strike',
  'script',
  'color', 'background',
  'align',
  'list', 'bullet', 'indent',
  'blockquote', 'code-block',
  'link', 'image', 'video',
];

// Raw HTML/CSS authoring control. Stores the body verbatim (no Quill
// normalization, which would strip <style>/classes) and shows a live preview.
// Slots into a Form.Item, which injects `value`/`onChange`.
const RawHtmlEditor: React.FC<{ value?: string; onChange?: (val: string) => void }> = ({ value, onChange }) => (
  <div>
    <TextArea
      value={value || ''}
      onChange={(e) => onChange?.(e.target.value)}
      rows={18}
      placeholder={'Paste full HTML/CSS here — including <style> blocks…'}
      style={{ fontFamily: 'monospace', fontSize: 13 }}
    />
    <Typography.Text type="secondary" style={{ display: 'block', margin: '8px 0 4px' }}>
      Live preview
    </Typography.Text>
    <iframe
      title="raw-html-preview"
      srcDoc={value || ''}
      sandbox="allow-same-origin"
      style={{ width: '100%', height: 360, border: '1px solid #eee', borderRadius: 6, background: '#fff' }}
    />
  </div>
);

const CreateContentPage: React.FC = () => {
  // const location = useLocation();
  // const state = location.state as LocationState;
  // const record = state && state.record;

  const [isUpdating, setIsUpdating] = useState(false)
  const [imagePreviewUrl, setImagePreviewUrl] = useState(null);
  const [isRawHtml, setIsRawHtml] = useState(false);

  const { state } = useLocation();
  const record = state && (state as LocationState).record;

  useEffect(() => {
    if (record) {
      console.log("IS UPDATING")
      setIsUpdating(true)
      setImagePreviewUrl(record.image)
      setIsRawHtml(!!record.is_raw_html)
    }
  }, [record])

  const navigate = useNavigate();

  // DatePicker needs a dayjs value, so convert the stored 'YYYY-MM-DD' string.
  const initialValues = record
    ? { ...record, publish_date: record.publish_date ? dayjs(record.publish_date) : null }
    : undefined;

  const onFinish = (values: any) => {
    const combinedValues = { ...record, ...values }
    console.log('combinedValues: ', combinedValues);
    return new Promise(() => {
      const bodyFormData = new FormData();
      bodyFormData.append('title', combinedValues.title);
      bodyFormData.append('body', combinedValues.body);
      bodyFormData.append('available_in_about_screen', combinedValues.available_in_about_screen);
      bodyFormData.append('available_in_explore_screen', combinedValues.available_in_explore_screen);
      bodyFormData.append('brand', combinedValues.brand)
      bodyFormData.append('is_raw_html', combinedValues.is_raw_html ? 'true' : 'false');
      // Scheduled publish date (empty string clears it → backend stores null).
      const pd = combinedValues.publish_date;
      const publishStr = pd ? (typeof pd === 'string' ? pd : pd.format('YYYY-MM-DD')) : '';
      bodyFormData.append('publish_date', publishStr);
      if (combinedValues.image && combinedValues.image.file) {
        bodyFormData.append('image', combinedValues.image.file);
      } else {
        console.error('No valid image file found.');
      }

      if (isUpdating) {
        updateContent(record.id, bodyFormData)
          .then((res) => {
            console.log('res', res);
            notificationController.success({ message: 'Content updated successfully.' });
            navigate(-1);
          })
          .catch((error) => {
            console.log('error: ', error);
            notificationController.error({ message: error });
          });
      } else {
        postContent(bodyFormData)
          .then((res) => {
            console.log('res', res);
            notificationController.success({ message: 'Content uploaded successfully.' });
            navigate(-1);
          })
          .catch((error) => {
            console.log('error: ', error);
            notificationController.error({ message: error });
          });
      }
    });
  };

  const props: UploadProps = {
    beforeUpload: (file: any) => {
      const imageTypes = ['image/png', 'image/jpeg', 'image/gif', 'image/bmp', 'image/webp', 'image/svg+xml', 'image/tiff'];
      const isImage = imageTypes.includes(file.type);
      if (!isImage) {
        message.error(`${file.name} is not a valid image file`);
        return Upload.LIST_IGNORE
      }
      return false
    },
    onChange: (info) => {
      console.log("Upload onChange: ", info);
      const file = info.file;
      if (file) {
        setImagePreviewUrl(URL.createObjectURL(file));
      }
    },
  };

  return (
    <Space size={'large'} direction={'vertical'}>
      <Typography.Title level={3}>Content</Typography.Title>
      <Card>
        <Form
          name={'notification-form'}
          layout={'vertical'}
          initialValues={initialValues}
          onFinish={onFinish}
          autoComplete={'off'}
        >
          <Row gutter={{ xs: 10, md: 15, xl: 30 }}>
            <Col xs={24}>
              <Form.Item
                name={'title'}
                label={'Title'}
                rules={[{ required: true, message: 'Please input a title' }]}
                style={{ maxWidth: '40rem' }}
              >
                <Input />
              </Form.Item>
            </Col>
            <Space size={'middle'} direction="vertical">
              {/* {initialContentValues && <Image style={{ marginLeft: 16 }} height={140} src={initialContentValues.file} />} */}
              <Col xs={24}>
                <Form.Item
                  name={'image'}
                  label={'Thumbnail'}
                  rules={[{ required: true, message: 'Please upload an image' }]}>
                  <Upload maxCount={1} {...props}>
                    <Button icon={<UploadOutlined />}>{isUpdating ? 'Change Image' : 'Upload Image'}</Button>
                  </Upload>
                </Form.Item>
                {imagePreviewUrl && <Image preview={false} height={200} src={getProfilePhoto(imagePreviewUrl)} />}
              </Col>
            </Space>
            <Col xs={24}>
              <Form.Item
                name={'is_raw_html'}
                label={'Editor mode'}
                valuePropName="checked"
                initialValue={false}
              >
                <Switch
                  checkedChildren="Raw HTML/CSS"
                  unCheckedChildren="Rich text"
                  onChange={setIsRawHtml}
                />
              </Form.Item>
            </Col>
            <Col xs={24}>
              <Form.Item
                name={'body'}
                label={'Content'}
                rules={[{ required: true, message: 'Please input a description' }]}
              >
                {isRawHtml ? (
                  <RawHtmlEditor />
                ) : (
                  <ReactQuill
                    modules={quillModules}
                    formats={quillFormats}
                  />
                )}
              </Form.Item>
            </Col>
            <Col xs={24}>
              <Form.Item
                label={'Brand'}
                name="brand"
                initialValue="ease"
                rules={[{ required: true, message: "Please select an option" }]}
              >
                <Radio.Group>
                  <Radio value="ease">Ease</Radio>
                  <Radio value="wavon">Wavon</Radio>
                </Radio.Group>
              </Form.Item>
            </Col>
            <Col xs={24}>
              <Form.Item
                name={'publish_date'}
                label={'Publish date'}
                extra={'Leave blank to publish immediately. A future date hides the article in the app until that day.'}
              >
                <DatePicker
                  style={{ maxWidth: '20rem', width: '100%' }}
                  placeholder={'Publish immediately'}
                  getPopupContainer={(t) => t.parentElement as HTMLElement}
                />
              </Form.Item>
            </Col>
            <Col xs={24}>
              <Form.Item name={'available_in_about_screen'} valuePropName="checked" rules={[{ required: true }]}>
                <Checkbox>About Us</Checkbox>
              </Form.Item>
              <Form.Item name={'available_in_explore_screen'} valuePropName="checked" rules={[{ required: true }]}>
                <Checkbox>Explore</Checkbox>
              </Form.Item>
            </Col>
            <Col xs={24} md={6}>
              <Form.Item wrapperCol={{ span: 24 }}>
                <Button type={'ghost'} block htmlType={'submit'}>
                  {isUpdating ? 'Update' : 'Post'}
                </Button>
              </Form.Item>
            </Col>
            <Col xs={24} md={6}>
              <Form.Item wrapperCol={{ span: 24 }}>
                <Button danger type={'ghost'} block onClick={() => navigate(-1)}>
                  Cancel
                </Button>
              </Form.Item>
            </Col>
          </Row>
        </Form>
      </Card>
    </Space>
  );
};

export default CreateContentPage;
